import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  Check,
  Circle,
  Info,
  Plus,
  Minus,
  X,
  ChevronDown,
} from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import useReveal from "../lib/useReveal";
import { CALENDLY_URL } from "../lib/constants";

/* ------------------------------- Constants ------------------------------- */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKS_PER_MONTH = 4.333;

const ROLES = [
  { role: "Admin / office assistant", salary: 48310 },
  { role: "Bookkeeper / accounting clerk", salary: 50670 },
  { role: "Dispatcher / customer service", salary: null },
  { role: "Office manager", salary: null },
  { role: "Other role", salary: null },
];

const PILLARS = [
  { n: "01", name: "Financial Admin", desc: "QuickBooks cleanup, P&L formatting, breakeven & variance tracking" },
  { n: "02", name: "Call Operations", desc: "Post-call recap docs, follow-up email drafting, agenda prep" },
  { n: "03", name: "CRM & Lead Ops", desc: "GoHighLevel/HubSpot setup, Google LSA & GBP, lead pipeline" },
  { n: "04", name: "Pricing Support", desc: "Estimate sheets, job cost templates, margin dashboards" },
  { n: "05", name: "HR & People Admin", desc: "Offer letters, onboarding checklists, commission plan docs" },
];

// Published pillar coverage matrix — reads directly off /pricing:
// state values: "full" | "partial" | "none"
const PLAN_MATRIX = {
  Starter: { 
    price: 1500, capacity: 20, per: "20 hrs/week",
    bestFor: "Solo operator, 1–3 people",
    pillars: { "01": "partial", "02": "full", "03": "none", "04": "none", "05": "none" },
  },
  Growth: {
    price: 2750, capacity: 40, per: "40 hrs/week",
    bestFor: "5–15 people, $750K–$2M revenue",
    pillars: { "01": "full", "02": "full", "03": "full", "04": "none", "05": "none" },
  },
  Operator: {
    price: 4500, capacity: 60, per: "60 hrs/week",
    bestFor: "15+ employees, scaling past $2M",
    pillars: { "01": "full", "02": "full", "03": "full", "04": "full", "05": "full" },
  },
  Enterprise: {
    price: null, capacity: null, per: "Dedicated team",
    bestFor: "Multi-location or $5M+",
    pillars: { "01": "full", "02": "full", "03": "full", "04": "full", "05": "full" },
  },
};

const PLAN_ORDER = ["Starter", "Growth", "Operator", "Enterprise"];

const TEAM_SIZES = [
  { value: "1-3", label: "1–3 people" },
  { value: "5-15", label: "5–15 people" },
  { value: "15+", label: "15+ people" },
  { value: "multi", label: "Multi-location" },
];

/* ------------------------------- Utilities ------------------------------- */

const fmtMoney = (n) => (n == null || !isFinite(n) ? "—" : "$" + Math.round(n).toLocaleString("en-US"));
const fmtPct = (p) => (p == null || !isFinite(p) ? "—" : `${Math.round(p * 100)}%`);

const cleanNumber = (raw) => {
  if (raw === "" || raw == null) return 0;
  const cleaned = String(raw).replace(/[$,\s]/g, "");
  const n = Number(cleaned);
  if (!isFinite(n) || n < 0) return 0;
  return n;
};

/* ---------------------------- Small primitives --------------------------- */

const Eyebrow = ({ children, tone = "green" }) =>
  tone === "green" ? (
    <span className="inline-flex items-center rounded-full bg-[#E7F6EE] border border-[#16A06A]/20 px-3.5 py-1.5">
      <span className="eyebrow text-[#16A06A]">{children}</span>
    </span>
  ) : (
    <span className="eyebrow text-slate-400">{children}</span>
  );

const NumberInput = ({ value, onChange, prefix, suffix, min = 0, max, "data-testid": testid, className = "", placeholder = "" }) => (
  <div className={`inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white focus-within:border-[#16A06A] focus-within:ring-1 focus-within:ring-[#16A06A]/30 transition-colors ${className}`}>
    {prefix && <span className="pl-3 pr-1 text-slate-400 text-[14px]">{prefix}</span>}
    <input
      data-testid={testid}
      type="text"
      inputMode="numeric"
      value={value === 0 && placeholder ? "" : value}
      placeholder={placeholder}
      onChange={(e) => {
        const n = cleanNumber(e.target.value);
        const bounded = max != null ? Math.min(Math.max(n, min), max) : Math.max(n, min);
        onChange(bounded);
      }}
      className="w-full bg-transparent px-3 py-2.5 text-[15px] text-[#0A1628] focus:outline-none"
    />
    {suffix && <span className="pr-3 pl-1 text-slate-400 text-[13px]">{suffix}</span>}
  </div>
);

const Slider = ({ value, onChange, min = 0, max = 40, step = 1, "data-testid": testid }) => (
  <input
    type="range"
    data-testid={testid}
    min={min}
    max={max}
    step={step}
    value={value}
    onChange={(e) => onChange(Number(e.target.value))}
    className="w-full accent-[#16A06A] h-1.5 rounded-lg bg-slate-200"
  />
);

const Stepper = ({ value, onChange, min = 1, "data-testid": testid }) => (
  <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white">
    <button
      type="button"
      onClick={() => onChange(Math.max(min, value - 1))}
      data-testid={testid ? `${testid}-minus` : undefined}
      className="h-10 w-10 inline-flex items-center justify-center text-slate-500 hover:text-[#16A06A] transition-colors"
      aria-label="Decrease"
    >
      <Minus size={14} />
    </button>
    <span data-testid={testid} className="min-w-[2rem] text-center text-[15px] font-semibold text-[#0A1628]">{value}</span>
    <button
      type="button"
      onClick={() => onChange(value + 1)}
      data-testid={testid ? `${testid}-plus` : undefined}
      className="h-10 w-10 inline-flex items-center justify-center text-slate-500 hover:text-[#16A06A] transition-colors"
      aria-label="Increase"
    >
      <Plus size={14} />
    </button>
  </div>
);

const SegChip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`flex-1 px-4 py-2.5 text-[14px] font-semibold rounded-md transition-all ${
      active
        ? "bg-white text-[#0A1628] shadow-sm"
        : "text-slate-500 hover:text-[#0A1628]"
    }`}
  >
    {children}
  </button>
);

/* --------------------------------- Hero ---------------------------------- */

const Hero = () => (
  <section data-testid="calc-hero" className="relative pt-32 lg:pt-36 pb-14 lg:pb-16">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
      <div className="absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-[rgba(22,160,106,0.08)] blur-3xl" />
      <div className="absolute top-32 -left-40 h-[360px] w-[360px] rounded-full bg-[rgba(10,22,40,0.05)] blur-3xl" />
    </div>
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8 reveal">
      <Eyebrow>Back office math</Eyebrow>
      <h1 className="h1-hero mt-5 text-[#0A1628] max-w-4xl">
        What is your back office{" "}
        <span className="text-[#16A06A]">actually costing you?</span>
      </h1>
      <p className="mt-6 text-[18px] leading-[1.65] text-slate-500 max-w-3xl">
        Your hours, your admin help, the software, the hiring, the covering
        when someone leaves. Put your real numbers in and see the whole figure
        next to a flat monthly plan.
      </p>
      <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-slate-500">
        <span>No email required</span>
        <span className="text-slate-300">·</span>
        <span>Nothing saved unless you ask</span>
        <span className="text-slate-300">·</span>
        <span>Takes about a minute</span>
      </div>
    </div>
  </section>
);

/* -------------------------------- Section 5 ------------------------------ */

const Sources = () => (
  <section data-testid="calc-sources" className="py-16 lg:py-20">
    <div className="mx-auto max-w-[1100px] px-6 lg:px-8 reveal">
      <details className="rounded-2xl border border-slate-200 bg-white card-soft overflow-hidden group">
        <summary className="cursor-pointer list-none px-6 md:px-8 py-5 flex items-center justify-between gap-4 hover:bg-[#FAFAFA] transition-colors">
          <span className="text-[16px] font-semibold text-[#0A1628]">Where these numbers come from</span>
          <ChevronDown size={18} className="text-slate-400 group-open:rotate-180 transition-transform" />
        </summary>
        <div className="px-6 md:px-8 pb-7 pt-2">
          <dl className="divide-y divide-slate-200 text-[14px]">
            {[
              ["Employer burden, 43% on top of base pay", "US Bureau of Labor Statistics, Employer Costs for Employee Compensation, March 2026 — benefits are 30.1% of total compensation for private-industry workers, wages 69.9%. Editable above."],
              ["Admin / office assistant, $48,310 a year", "BLS Occupational Outlook Handbook — secretaries and administrative assistants, May 2025 median. Editable above."],
              ["Bookkeeper / accounting clerk, $50,670 a year", "BLS Occupational Outlook Handbook, May 2025 median for the bookkeeping and accounting clerk category. Editable above."],
              ["Owner admin time, about 16 hours a week", "Time Etc; The Alternative Board. Shown as context only — it is not the slider default, and it is not a claim about what a plan returns to you."],
              ["Plan prices, weekly capacity and pillar coverage", "RevGen's published pricing at revgenworkforce.com/pricing."],
              ["Weeks in a month, 4.33", "52 ÷ 12."],
            ].map(([label, source]) => (
              <div key={label} className="grid md:grid-cols-12 gap-3 py-4">
                <dt className="md:col-span-4 text-[#0A1628] font-medium">{label}</dt>
                <dd className="md:col-span-8 text-slate-500 leading-relaxed">{source}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-6 text-[12px] text-slate-400 leading-relaxed">
            An estimate built from the figures you enter and the published medians above. Not a quote. Your actual numbers get reviewed on the assessment call.
          </p>
          <p className="mt-3 text-[13px] text-slate-500 leading-relaxed">
            We prepare and clean; we don't sign your return. Anything requiring a licensed CPA, attorney, or tax filing is out of scope.
          </p>
        </div>
      </details>
    </div>
  </section>
);

/* -------------------------------- Section 6 ------------------------------ */

const WeEmploy = () => (
  <section data-testid="calc-we-employ" className="py-24 lg:py-28 bg-[#F8FAFC]">
    <div className="mx-auto max-w-[1100px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">How It Works</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">We employ. You direct.</h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          You get a trained operator working inside your business — without
          payroll, contracts, hiring, or HR admin landing on your desk. We
          carry the employment. You set the priorities.
        </p>
      </div>
      <div className="reveal mt-12 rounded-2xl bg-white border border-slate-200 card-soft p-7 lg:p-10">
        <div className="flex flex-col md:flex-row md:items-stretch md:gap-4">
          <div data-testid="calc-node-owner" className="flex-1 rounded-2xl border border-slate-200 bg-white card-soft p-6 lg:p-7 text-center">
            <div className="mx-auto h-11 w-11 rounded-full flex items-center justify-center text-white bg-[#0A1628]">
              <Check size={18} strokeWidth={3} />
            </div>
            <div className="mt-4 text-[16px] font-semibold text-[#0A1628]">You</div>
            <div className="mt-1 text-[13px] text-slate-500">Owner / Operator</div>
          </div>
          <div className="my-2 md:my-0 md:flex md:items-center md:justify-center px-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#16A06A]">Directs the work →</span>
          </div>
          <div data-testid="calc-node-operator" className="flex-1 rounded-2xl border border-[#16A06A]/20 bg-[#E7F6EE] card-soft p-6 lg:p-7 text-center">
            <div className="mx-auto h-11 w-11 rounded-full flex items-center justify-center text-white bg-[#16A06A]">
              <Check size={18} strokeWidth={3} />
            </div>
            <div className="mt-4 text-[16px] font-semibold text-[#0A1628]">Your RevGen Operator</div>
            <div className="mt-1 text-[13px] text-slate-500">Works inside your tools</div>
          </div>
          <div className="my-2 md:my-0 md:flex md:items-center md:justify-center px-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#16A06A]">← Employs · Trains · Pays</span>
          </div>
          <div data-testid="calc-node-revgen" className="flex-1 rounded-2xl border border-slate-200 bg-white card-soft p-6 lg:p-7 text-center">
            <div className="mx-auto h-11 w-11 rounded-full flex items-center justify-center text-white bg-[#0A1628]">
              <Check size={18} strokeWidth={3} />
            </div>
            <div className="mt-4 text-[16px] font-semibold text-[#0A1628]">RevGen</div>
            <div className="mt-1 text-[13px] text-slate-500">Employment & training</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* --------------------------------- FAQ ---------------------------------- */

const FAQS = [
  { q: "Is this a quote?", a: "No. It's your own numbers against published plan prices. What a plan actually needs to cover gets scoped on the assessment call, and the price you see here is the price on the pricing page." },
  { q: "Why is my own time in the calculation?", a: "Because it's the largest number in most trades businesses and it never shows up on a P&L. If you're doing ten hours a week of invoicing and follow-up, that time has a price whether or not anyone bills you for it." },
  { q: "What if the plan costs more than what I spend today?", a: "Then it will say so. The fee covers the work and everything around it — hiring, employing, training and covering the person. If that's not worth it yet, start with a fixed-fee project instead." },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="calc-faq" className="py-24 lg:py-28">
      <div className="mx-auto max-w-[1100px] px-6 lg:px-8">
        <div className="max-w-2xl reveal">
          <Eyebrow tone="muted">FAQ</Eyebrow>
          <h2 className="h2-section mt-3 text-[#0A1628]">Three questions people ask here.</h2>
        </div>
        <div className="reveal mt-10 rounded-2xl border border-slate-200 bg-white card-soft divide-y divide-slate-200 overflow-hidden">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q} data-testid={`calc-faq-item-${i + 1}`}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  data-testid={`calc-faq-toggle-${i + 1}`}
                  className="w-full flex items-center justify-between gap-6 px-6 md:px-8 py-6 text-left hover:bg-[#FAFAFA] transition-colors"
                >
                  <span className="text-[16px] md:text-[17px] font-semibold text-[#0A1628]">{f.q}</span>
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${isOpen ? "bg-[#16A06A] text-white" : "bg-[#E7F6EE] text-[#16A06A]"}`}>
                    {isOpen ? <Minus size={16} strokeWidth={3} /> : <Plus size={16} strokeWidth={3} />}
                  </span>
                </button>
                <div className={`grid transition-all duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="overflow-hidden">
                    <p className="px-6 md:px-8 pb-6 text-[15px] md:text-[16px] leading-relaxed text-slate-500">{f.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------ Final CTA -------------------------------- */

const FinalCTA = () => (
  <section data-testid="calc-final-cta" className="relative bg-[#0A1628] overflow-hidden">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute -top-24 left-1/4 h-[360px] w-[360px] rounded-full bg-[#16A06A]/15 blur-3xl" />
      <div className="absolute -bottom-32 right-10 h-[420px] w-[420px] rounded-full bg-[#16A06A]/10 blur-3xl" />
    </div>
    <div className="relative mx-auto max-w-[1100px] px-6 lg:px-8 py-24 lg:py-28 text-center reveal">
      <h2 className="h2-section text-white max-w-3xl mx-auto">
        Numbers on the screen, or someone actually{" "}
        <span className="text-[#34D399]">looking at your books?</span>
      </h2>
      <p className="mt-5 text-[17px] lg:text-[18px] leading-relaxed text-slate-300 max-w-2xl mx-auto">
        Book a free 30-minute back office assessment. We'll go through where
        your time is going and tell you which plan fits — or if a project is
        the smarter start.
      </p>
      <div className="mt-9 flex justify-center">
        <a
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="calc-final-cta-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-[#16A06A] hover:bg-[#0E7C53] text-white px-6 py-3.5 text-[15px] font-semibold transition-colors"
        >
          Get a Free Back Office Assessment
          <ArrowRight size={16} />
        </a>
      </div>
    </div>
  </section>
);

/* =========================================================================
   MAIN CALCULATOR — inputs left, sticky results right
   ========================================================================= */

const DEFAULT_STAFF_ROW = () => ({
  role: "Admin / office assistant",
  count: 1,
  annualBaseSalary: 48310,
  handoverHoursPerWeek: 5,
});

function computeResults(inputs) {
  const {
    mode, ownerHoursPerWeek, ownerHourlyValue, burdenPct,
    staff, softwarePerMonth, workspacePerMonth, turnover, pillars, teamSize,
  } = inputs;

  const staffActive = mode !== "self";
  const ownerActive = mode !== "in-house";

  const ownerMonthly = ownerActive
    ? ownerHoursPerWeek * WEEKS_PER_MONTH * ownerHourlyValue
    : 0;

  const staffMonthly = staffActive
    ? staff.reduce(
        (sum, r) => sum + r.count * r.annualBaseSalary * (1 + burdenPct),
        0
      ) / 12
    : 0;

  const turnoverMonthly = staffActive && turnover.enabled && turnover.costPerReplacement && turnover.yearsBetween
    ? turnover.costPerReplacement / (turnover.yearsBetween * 12)
    : 0;

  const overheadMonthly = softwarePerMonth + workspacePerMonth;
  const todayMonthly = ownerMonthly + staffMonthly + turnoverMonthly + overheadMonthly;
  const todayAnnual = todayMonthly * 12;

  const handoverHoursPerWeek =
    (ownerActive ? ownerHoursPerWeek : 0) +
    (staffActive ? staff.reduce((s, r) => s + r.handoverHoursPerWeek, 0) : 0);

  // 1 — capacity floor
  let capacityTier;
  if (handoverHoursPerWeek <= 20) capacityTier = "Starter";
  else if (handoverHoursPerWeek <= 40) capacityTier = "Growth";
  else if (handoverHoursPerWeek <= 60) capacityTier = "Operator";
  else capacityTier = "Enterprise";

  // 2 — pillar floor
  const has = (p) => pillars.includes(p);
  let pillarTier;
  if (has("04") || has("05")) pillarTier = "Operator";
  else if (has("03")) pillarTier = "Growth";
  else pillarTier = "Starter";

  // 3 — team size floor
  const sizeTier = { "1-3": "Starter", "5-15": "Growth", "15+": "Operator", "multi": "Enterprise" }[teamSize];

  const recommendedPlan = [capacityTier, pillarTier, sizeTier].reduce(
    (a, b) => (PLAN_ORDER.indexOf(a) >= PLAN_ORDER.indexOf(b) ? a : b)
  );

  const plan = PLAN_MATRIX[recommendedPlan];
  const planFee = plan.price;
  const revgenMonthly = planFee != null ? planFee + overheadMonthly : null;
  const revgenAnnual = revgenMonthly != null ? revgenMonthly * 12 : null;

  const monthlyDifference = revgenMonthly != null ? todayMonthly - revgenMonthly : null;
  const annualDifference = monthlyDifference != null ? monthlyDifference * 12 : null;
  const percentLower = monthlyDifference != null && todayMonthly > 0 ? monthlyDifference / todayMonthly : null;

  return {
    ownerMonthly, staffMonthly, turnoverMonthly, overheadMonthly,
    todayMonthly, todayAnnual,
    recommendedPlan, planFee,
    revgenMonthly, revgenAnnual,
    monthlyDifference, annualDifference, percentLower,
    handoverHoursPerWeek, planCapacity: plan.capacity,
    tiers: { capacityTier, pillarTier, sizeTier },
  };
}

/* ------------------------------- Results panel -------------------------- */

const ResultsPanel = ({ inputs, results, emailEnabled }) => {
  const {
    todayMonthly, todayAnnual, revgenMonthly, revgenAnnual,
    monthlyDifference, annualDifference, percentLower,
    recommendedPlan, planFee, handoverHoursPerWeek, planCapacity,
    ownerMonthly, staffMonthly, turnoverMonthly,
  } = results;

  const isEnterprise = recommendedPlan === "Enterprise";
  const isPositive = !isEnterprise && monthlyDifference != null && monthlyDifference > 0;
  const isNegative = !isEnterprise && monthlyDifference != null && monthlyDifference <= 0;

  return (
    <div className="space-y-5">
      {/* Card 1 — Headline */}
      <div
        data-testid="calc-headline-card"
        className={`rounded-2xl p-6 lg:p-7 card-soft ${
          isNegative ? "border border-slate-200 bg-[#FAFAFA]" : "border border-[#16A06A]/25 bg-white"
        }`}
        aria-live="polite"
      >
        {isEnterprise ? (
          <>
            <p className="eyebrow text-slate-400">Recommended</p>
            <p className="mt-2 text-[32px] lg:text-[36px] font-bold tracking-tight text-[#0A1628] leading-tight">
              Custom — scoped to your business
            </p>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="calc-headline-enterprise-cta"
              className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#16A06A] hover:bg-[#0E7C53] text-white px-5 py-3 text-[14px] font-semibold transition-colors"
            >
              Talk to us <ArrowRight size={14} />
            </a>
          </>
        ) : isPositive ? (
          <>
            <p className="eyebrow text-slate-400">The difference, per year</p>
            <p data-testid="calc-headline-annual" className="mt-3 text-[44px] lg:text-[54px] font-bold tracking-tight text-[#0A1628] leading-none">
              {fmtMoney(annualDifference)}
            </p>
            <div className="mt-3 flex items-center gap-3">
              <span className="text-[15px] text-slate-500">{fmtMoney(monthlyDifference)} a month</span>
              <span data-testid="calc-headline-pct" className="inline-flex items-center rounded-full bg-[#E7F6EE] border border-[#16A06A]/20 text-[#16A06A] text-[12px] font-bold px-2.5 py-1">
                {fmtPct(percentLower)} lower
              </span>
            </div>
            <div className="mt-5 pt-5 border-t border-slate-100">
              <p className="text-[13px] text-slate-500">Recommended plan</p>
              <p className="mt-1 text-[18px] font-semibold">
                <span className="text-[#16A06A]" data-testid="calc-headline-plan">{recommendedPlan}</span>
                <span className="text-[#0A1628]"> — {fmtMoney(planFee)}/mo</span>
              </p>
              <p className="mt-1 text-[13px] text-slate-400">Renews monthly. Cancel with 30 days' notice. No setup fees.</p>
            </div>
          </>
        ) : (
          <>
            <p className="eyebrow text-slate-400">On these numbers</p>
            <p data-testid="calc-headline-negative" className="mt-3 text-[15px] leading-relaxed text-[#0A1628]">
              A plan costs more per month than what you're spending today. What the fee buys is the part that isn't on your invoice — recruiting, employment, training, backup coverage and documented processes. If the hours are the problem more than the invoice, a fixed-fee project is the smarter start.
            </p>
            <Link
              to="/pricing#projects"
              data-testid="calc-headline-projects-link"
              className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#16A06A] hover:text-[#0E7C53]"
            >
              See fixed-fee projects <ArrowRight size={14} />
            </Link>
          </>
        )}
      </div>

      {/* Hours line */}
      <p data-testid="calc-hours-line" className="text-[13px] text-slate-500">
        You described about <strong className="text-[#0A1628]">{Math.round(handoverHoursPerWeek)} hours a week</strong> of back office work.{" "}
        {planCapacity != null ? (
          <>{recommendedPlan}'s planned capacity is <strong className="text-[#0A1628]">{planCapacity} hrs/week</strong>.</>
        ) : (
          <>{recommendedPlan} is scoped to your business.</>
        )}
      </p>
      {planCapacity != null && handoverHoursPerWeek > planCapacity && (
        <p data-testid="calc-over-capacity" className="text-[13px] text-slate-500">
          That's above this plan's planned capacity. The next plan up may be the better fit — worth scoping on the call.
        </p>
      )}

      {/* Card 2 — Line by line */}
      <div data-testid="calc-linebyline-card" className="rounded-2xl border border-slate-200 bg-white card-soft overflow-hidden">
        <div className="px-5 md:px-6 py-4 border-b border-slate-200">
          <p className="eyebrow text-slate-400">Line by line</p>
          <p className="mt-1 text-[16px] font-semibold text-[#0A1628]">What you're paying now</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] md:text-[14px]">
            <thead>
              <tr className="border-b border-slate-200 bg-[#F8FAFC]">
                <th className="sticky left-0 z-10 bg-[#F8FAFC] text-left px-5 md:px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">What it is</th>
                <th className="text-left px-5 md:px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Today</th>
                <th className="text-left px-5 md:px-6 py-3 text-[11px] uppercase tracking-wider text-[#16A06A] font-semibold bg-[#E7F6EE]">With RevGen</th>
              </tr>
            </thead>
            <tbody>
              {[
                ownerMonthly > 0 && ["Your hours on back office work", fmtMoney(ownerMonthly), "Inside the fee"],
                staffMonthly > 0 && ["In-house admin pay, including employer burden", fmtMoney(staffMonthly), "Inside the fee"],
                turnoverMonthly > 0 && ["Recruiting and replacing people", fmtMoney(turnoverMonthly), "Inside the fee"],
                inputs.softwarePerMonth > 0 && ["Software and subscriptions", fmtMoney(inputs.softwarePerMonth), `${fmtMoney(inputs.softwarePerMonth)} — unchanged`],
                inputs.workspacePerMonth > 0 && ["Workspace, equipment and phones", fmtMoney(inputs.workspacePerMonth), `${fmtMoney(inputs.workspacePerMonth)} — unchanged`],
              ]
                .filter(Boolean)
                .map((row, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="sticky left-0 z-10 bg-white px-5 md:px-6 py-3.5 text-[#0A1628]">{row[0]}</td>
                    <td className="px-5 md:px-6 py-3.5 text-slate-600">{row[1]}</td>
                    <td className="px-5 md:px-6 py-3.5 text-[#0A1628] bg-[#E7F6EE]/70">{row[2]}</td>
                  </tr>
                ))}
              <tr className="border-t border-slate-200">
                <td className="sticky left-0 z-10 bg-white px-5 md:px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold" colSpan={3}>
                  What isn't on your invoice today
                </td>
              </tr>
              {[
                ["Recruiting and vetting", "Yours to run", "Included — we find and screen the person"],
                ["Employment, payroll and benefits", "Yours", "Included — carried entirely by RevGen, one invoice"],
                ["Trades-specific training", "You teach it", "Included — QuickBooks, ServiceTitan and GoHighLevel before day one"],
                ["Cover when they leave or aren't a fit", "You start over", "Included — replaced within two weeks"],
                ["Quality checks on the work", "—", "Included — a RevGen lead reviews output before it reaches you"],
                ["Written processes you own", "In your head", "Included — your own SOP library"],
                ["A named person to call", "—", "Included — one person, not a ticket queue"],
              ].map((row, i) => (
                <tr key={`b-${i}`} className="border-t border-slate-100">
                  <td className="sticky left-0 z-10 bg-white px-5 md:px-6 py-3.5 text-[#0A1628]">{row[0]}</td>
                  <td className="px-5 md:px-6 py-3.5 text-slate-500 text-[13px]">{row[1]}</td>
                  <td className="px-5 md:px-6 py-3.5 text-[#0A1628] bg-[#E7F6EE]/70">
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#16A06A] text-white">
                        <Check size={10} strokeWidth={3} />
                      </span>
                      {row[2]}
                    </span>
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-[#0A1628]">
                <td className="sticky left-0 z-10 bg-white px-5 md:px-6 py-4 font-bold text-[#0A1628] text-[14px]">Total</td>
                <td className="px-5 md:px-6 py-4 font-bold text-[#0A1628] text-[14px]">
                  {fmtMoney(todayMonthly)} <span className="text-[12px] text-slate-500 font-medium">/ mo</span>
                  <div className="text-[12px] font-semibold text-slate-500">{fmtMoney(todayAnnual)} / year</div>
                </td>
                <td className="px-5 md:px-6 py-4 font-bold text-[#0A1628] text-[14px] bg-[#E7F6EE]">
                  {revgenMonthly != null ? fmtMoney(revgenMonthly) : "Custom"} <span className="text-[12px] text-slate-500 font-medium">/ mo</span>
                  <div className="text-[12px] font-semibold text-slate-500">{revgenAnnual != null ? `${fmtMoney(revgenAnnual)} / year` : "—"}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Card 3 — Plan coverage */}
      <PlanCoverage recommendedPlan={recommendedPlan} checkedPillars={inputs.pillars} />

      {/* Card 4 — CTA */}
      <NextActions emailEnabled={emailEnabled} inputs={inputs} results={results} />
    </div>
  );
};

/* --------------------------- Card 3 — Plan coverage --------------------- */

const PlanCoverage = ({ recommendedPlan, checkedPillars }) => {
  const plan = PLAN_MATRIX[recommendedPlan];
  return (
    <div data-testid="calc-plan-card" className="rounded-2xl border border-slate-200 bg-white card-soft p-6 lg:p-7">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow text-slate-400">Recommended plan covers</p>
          <p className="mt-1 text-[20px] font-bold text-[#0A1628]">
            <span className="text-[#16A06A]">{recommendedPlan}</span>
            {plan.price != null && <span> — {fmtMoney(plan.price)}/mo</span>}
          </p>
        </div>
        <div className="text-[13px] font-semibold text-slate-500">{plan.per}</div>
      </div>
      <ul className="mt-5 space-y-3">
        {PILLARS.map((p) => {
          const state = plan.pillars[p.n];
          const checked = checkedPillars.includes(p.n);
          const StateIcon = state === "full" ? (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#16A06A] text-white">
              <Check size={12} strokeWidth={3} />
            </span>
          ) : state === "partial" ? (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border-2 border-[#16A06A] text-[#16A06A]">
              <Circle size={8} fill="currentColor" />
            </span>
          ) : (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 text-slate-400">
              <Minus size={10} />
            </span>
          );
          return (
            <li
              key={p.n}
              data-testid={`calc-pillar-${p.n}`}
              className={`flex items-start gap-3 text-[14px] rounded-lg px-2 py-1.5 -mx-2 ${
                checked ? "bg-[#E7F6EE]/50" : ""
              }`}
            >
              {StateIcon}
              <div>
                <span className="text-[#16A06A] font-bold text-[13px]">{p.n}</span>{" "}
                <span className="text-[#0A1628] font-medium">{p.name}</span>{" "}
                <span className="text-slate-400 text-[13px]">
                  · {state === "full" ? "Included" : state === "partial" ? "Partial" : "—"}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      {checkedPillars.some((p) => plan.pillars[p] === "none") && (
        <p data-testid="calc-plan-gap-note" className="mt-5 text-[13px] leading-relaxed text-slate-500">
          {(() => {
            const missing = checkedPillars.find((p) => plan.pillars[p] === "none");
            const missingName = PILLARS.find((p) => p.n === missing)?.name;
            const nextPlan = PLAN_ORDER[Math.min(PLAN_ORDER.indexOf(recommendedPlan) + 1, PLAN_ORDER.length - 1)];
            return `${missingName} isn't in this plan — the calculator has moved you up to ${nextPlan}, which covers it.`;
          })()}
        </p>
      )}
      <p className="mt-5 text-[13px] text-slate-500">{plan.bestFor}</p>
      <p className="mt-4 text-[12px] text-slate-400 leading-relaxed">
        Hours shown are planned capacity, not a billing meter. You're buying scoped deliverables at a flat monthly fee.
      </p>
    </div>
  );
};

/* ---------------------------- Card 4 — Next actions --------------------- */

const NextActions = ({ emailEnabled, inputs, results }) => {
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending" || status === "sent") return;
    setStatus("sending");
    setMsg("");
    try {
      const res = await axios.post(`${API}/calculator-email`, {
        email,
        hp,
        inputs,
        results: {
          ownerMonthly: results.ownerMonthly,
          staffMonthly: results.staffMonthly,
          turnoverMonthly: results.turnoverMonthly,
          overheadMonthly: results.overheadMonthly,
          todayMonthly: results.todayMonthly,
          todayAnnual: results.todayAnnual,
          recommendedPlan: results.recommendedPlan,
          planFee: results.planFee,
          revgenMonthly: results.revgenMonthly,
          revgenAnnual: results.revgenAnnual,
          monthlyDifference: results.monthlyDifference,
          annualDifference: results.annualDifference,
          percentLower: results.percentLower,
          handoverHoursPerWeek: results.handoverHoursPerWeek,
          planCapacity: results.planCapacity,
        },
      });
      if (res.data?.ok) {
        setStatus("sent");
      } else {
        setStatus("error");
        setMsg("Couldn't send just now. Try again in a moment.");
      }
    } catch (err) {
      const code = err?.response?.status;
      setStatus("error");
      setMsg(code === 429 ? "Too many submissions. Try again in a minute." : "Couldn't send just now. Try again in a moment.");
    }
  };

  return (
    <div data-testid="calc-next-card" className="rounded-2xl border border-slate-200 bg-white card-soft p-6 lg:p-7">
      <p className="eyebrow text-slate-400">Next</p>
      <a
        href={CALENDLY_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="calc-primary-cta"
        className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#16A06A] hover:bg-[#0E7C53] text-white px-5 py-3 text-[14px] font-semibold transition-colors"
      >
        Book a Free Assessment <ArrowRight size={14} />
      </a>
      <p className="mt-2 text-[13px] text-slate-500">
        You'll leave the assessment with two or three specific recommendations.
      </p>
      <Link
        to="/pricing#projects"
        data-testid="calc-projects-link"
        className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#16A06A] hover:text-[#0E7C53]"
      >
        Start with a fixed-fee project → <ArrowRight size={12} />
      </Link>

      {emailEnabled && (
        <form onSubmit={submit} data-testid="calc-email-form" className="mt-6 pt-6 border-t border-slate-100">
          {status === "sent" ? (
            <p data-testid="calc-email-sent" className="text-[14px] text-[#16A06A] font-semibold">
              Sent. Check your inbox.
            </p>
          ) : (
            <>
              <label htmlFor="calc-email-input" className="block text-[13px] font-medium text-[#0A1628]">
                Want this breakdown in your inbox?
              </label>
              <div className="mt-2 flex flex-col sm:flex-row gap-2">
                <input
                  id="calc-email-input"
                  data-testid="calc-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-[14px] text-[#0A1628] focus:border-[#16A06A] focus:ring-1 focus:ring-[#16A06A]/30 focus:outline-none transition-colors"
                />
                {/* Honeypot — hidden from users, visible to bots */}
                <input
                  type="text"
                  name="hp"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={hp}
                  onChange={(e) => setHp(e.target.value)}
                  className="hidden"
                  data-testid="calc-email-honeypot"
                />
                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="calc-email-submit"
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-[14px] font-semibold text-[#0A1628] hover:border-[#16A06A] hover:text-[#16A06A] disabled:opacity-50 transition-colors"
                >
                  {status === "sending" ? "Sending…" : "Send it"}
                </button>
              </div>
              {msg && <p className="mt-2 text-[12px] text-slate-500">{msg}</p>}
            </>
          )}
        </form>
      )}
    </div>
  );
};

/* ------------------------------ Input blocks ---------------------------- */

const BlockA = ({ mode, setMode }) => (
  <div data-testid="calc-block-a" className="rounded-2xl border border-slate-200 bg-white card-soft p-6 lg:p-7">
    <p className="eyebrow text-slate-400">How the work gets done today</p>
    <div className="mt-4 inline-flex w-full max-w-xl rounded-lg bg-[#F1F5F9] p-1">
      <SegChip active={mode === "self"} onClick={() => setMode("self")} testid="calc-mode-self">
        I handle most of it myself
      </SegChip>
      <SegChip active={mode === "in-house"} onClick={() => setMode("in-house")} testid="calc-mode-in-house">
        I have someone in-house
      </SegChip>
      <SegChip active={mode === "mix"} onClick={() => setMode("mix")} testid="calc-mode-mix">
        A mix of both
      </SegChip>
    </div>
  </div>
);

const BlockB = ({ inputs, update }) => (
  <div data-testid="calc-block-b" className="rounded-2xl border border-slate-200 bg-white card-soft p-6 lg:p-7">
    <p className="eyebrow text-slate-400">Your time</p>
    <div className="mt-5">
      <div className="flex items-baseline justify-between gap-4">
        <label htmlFor="calc-owner-hours" className="text-[15px] text-[#0A1628] font-medium">
          Hours a week you spend on back office work
        </label>
        <span data-testid="calc-owner-hours-value" className="text-[28px] font-bold text-[#0A1628] leading-none">
          {inputs.ownerHoursPerWeek}
        </span>
      </div>
      <div className="mt-3">
        <Slider
          value={inputs.ownerHoursPerWeek}
          onChange={(n) => update({ ownerHoursPerWeek: n })}
          min={0}
          max={40}
          step={1}
          data-testid="calc-owner-hours-slider"
        />
        <div className="mt-1 flex justify-between text-[11px] text-slate-400">
          <span>0</span><span>40</span>
        </div>
      </div>
    </div>
    <div className="mt-6">
      <label className="text-[15px] text-[#0A1628] font-medium">What an hour of your time is worth</label>
      <div className="mt-2">
        <NumberInput
          prefix="$"
          value={inputs.ownerHourlyValue}
          onChange={(n) => update({ ownerHourlyValue: n })}
          data-testid="calc-owner-hourly"
          className="w-40"
        />
      </div>
      <p className="mt-2 text-[13px] text-slate-400">Not sure? Take your annual owner draw and divide it by 2,000.</p>
    </div>
    <p className="mt-5 text-[12px] text-slate-400 leading-relaxed">
      Third-party surveys put small-business owner admin time at roughly 16 hours a week — about a third of the working week (Time Etc; The Alternative Board).
    </p>
  </div>
);

const StaffRow = ({ row, index, update, remove, canRemove }) => {
  const roleDef = ROLES.find((r) => r.role === row.role);
  return (
    <div data-testid={`calc-staff-row-${index}`} className="rounded-xl border border-slate-200 bg-[#FAFAFA] p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]">
          <label className="text-[12px] text-slate-500 font-medium">Role</label>
          <select
            data-testid={`calc-staff-${index}-role`}
            value={row.role}
            onChange={(e) => {
              const newRole = e.target.value;
              const def = ROLES.find((r) => r.role === newRole);
              update({ role: newRole, annualBaseSalary: def?.salary ?? 0 });
            }}
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-[14px] text-[#0A1628] focus:border-[#16A06A] focus:ring-1 focus:ring-[#16A06A]/30 focus:outline-none"
          >
            {ROLES.map((r) => <option key={r.role} value={r.role}>{r.role}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[12px] text-slate-500 font-medium">How many</label>
          <div className="mt-1"><Stepper value={row.count} onChange={(n) => update({ count: n })} data-testid={`calc-staff-${index}-count`} /></div>
        </div>
        <div className="min-w-[160px]">
          <label className="text-[12px] text-slate-500 font-medium">Annual base salary</label>
          <div className="mt-1">
            <NumberInput
              prefix="$"
              value={row.annualBaseSalary}
              onChange={(n) => update({ annualBaseSalary: n })}
              placeholder={roleDef?.salary == null ? "Enter salary" : ""}
              data-testid={`calc-staff-${index}-salary`}
              className="w-full"
            />
          </div>
        </div>
        <div className="min-w-[130px]">
          <label className="text-[12px] text-slate-500 font-medium">Hours a week you'd hand over</label>
          <div className="mt-1">
            <NumberInput
              value={row.handoverHoursPerWeek}
              onChange={(n) => update({ handoverHoursPerWeek: n })}
              suffix="hrs"
              max={40}
              data-testid={`calc-staff-${index}-handover`}
              className="w-full"
            />
          </div>
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={remove}
            data-testid={`calc-staff-${index}-remove`}
            className="h-10 w-10 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
            aria-label="Remove role"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

const BlockC = ({ inputs, update, updateStaff, addStaff, removeStaff }) => (
  <div data-testid="calc-block-c" className="rounded-2xl border border-slate-200 bg-white card-soft p-6 lg:p-7">
    <div className="flex items-baseline justify-between gap-4 flex-wrap">
      <p className="eyebrow text-slate-400">In-house help</p>
      <button
        type="button"
        onClick={addStaff}
        disabled={inputs.staff.length >= 5}
        data-testid="calc-add-staff"
        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#16A06A] hover:text-[#0E7C53] disabled:opacity-30"
      >
        <Plus size={14} /> Add a role
      </button>
    </div>
    <div className="mt-5 space-y-3">
      {inputs.staff.map((r, i) => (
        <StaffRow
          key={i}
          row={r}
          index={i}
          update={(patch) => updateStaff(i, patch)}
          remove={() => removeStaff(i)}
          canRemove={inputs.staff.length > 1}
        />
      ))}
    </div>
    <div className="mt-6">
      <label className="text-[14px] text-[#0A1628] font-medium inline-flex items-center gap-2">
        Employer burden on top of base pay
        <span
          className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-slate-500 cursor-help"
          title="Payroll taxes, insurance, paid leave and benefits. BLS Employer Costs for Employee Compensation, March 2026: benefits are 30.1% of total compensation for private-industry workers, wages 69.9% — about 43% on top of wages. Edit it if you know your own number."
        >
          <Info size={12} />
        </span>
      </label>
      <div className="mt-2">
        <NumberInput
          value={Math.round(inputs.burdenPct * 100)}
          onChange={(n) => update({ burdenPct: Math.min(n, 200) / 100 })}
          suffix="%"
          data-testid="calc-burden"
          className="w-32"
        />
      </div>
    </div>
  </div>
);

const BlockD = ({ inputs, update, updateTurnover }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="calc-block-d" className="rounded-2xl border border-slate-200 bg-white card-soft overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="calc-block-d-toggle"
        className="w-full flex items-center justify-between gap-4 px-6 lg:px-7 py-5 text-left hover:bg-[#FAFAFA] transition-colors"
      >
        <span>
          <span className="eyebrow text-slate-400">Add your overhead</span>
          <span className="ml-2 text-[13px] text-slate-400">optional</span>
        </span>
        <ChevronDown size={18} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 lg:px-7 pb-6 pt-2 space-y-5 border-t border-slate-100">
          <div>
            <label className="text-[14px] text-[#0A1628] font-medium">Software and subscriptions the back office runs on</label>
            <div className="mt-2 flex items-center gap-2">
              <NumberInput prefix="$" suffix="/mo" value={inputs.softwarePerMonth} onChange={(n) => update({ softwarePerMonth: n })} data-testid="calc-software" className="w-44" />
            </div>
          </div>
          <div>
            <label className="text-[14px] text-[#0A1628] font-medium">Workspace, equipment and phones</label>
            <div className="mt-2 flex items-center gap-2">
              <NumberInput prefix="$" suffix="/mo" value={inputs.workspacePerMonth} onChange={(n) => update({ workspacePerMonth: n })} data-testid="calc-workspace" className="w-44" />
            </div>
          </div>
          <div>
            <label className="inline-flex items-center gap-3 cursor-pointer">
              <span
                data-testid="calc-turnover-toggle"
                role="switch"
                aria-checked={inputs.turnover.enabled}
                onClick={() => updateTurnover({ enabled: !inputs.turnover.enabled })}
                onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); updateTurnover({ enabled: !inputs.turnover.enabled }); }}}
                tabIndex={0}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${inputs.turnover.enabled ? "bg-[#16A06A]" : "bg-slate-300"}`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${inputs.turnover.enabled ? "translate-x-6" : "translate-x-1"}`} />
              </span>
              <span className="text-[14px] text-[#0A1628] font-medium">Include a turnover allowance</span>
            </label>
            {inputs.turnover.enabled && (
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[12px] text-slate-500 font-medium">Cost to recruit and replace one hire</label>
                  <div className="mt-1">
                    <NumberInput prefix="$" value={inputs.turnover.costPerReplacement ?? 0} onChange={(n) => updateTurnover({ costPerReplacement: n })} data-testid="calc-turnover-cost" className="w-full" placeholder="Enter cost" />
                  </div>
                </div>
                <div>
                  <label className="text-[12px] text-slate-500 font-medium">How often that happens</label>
                  <select
                    data-testid="calc-turnover-years"
                    value={inputs.turnover.yearsBetween ?? ""}
                    onChange={(e) => updateTurnover({ yearsBetween: e.target.value ? Number(e.target.value) : null })}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-[14px] text-[#0A1628] focus:border-[#16A06A] focus:ring-1 focus:ring-[#16A06A]/30 focus:outline-none"
                  >
                    <option value="">Choose…</option>
                    <option value="1">Once a year</option>
                    <option value="2">Every two years</option>
                    <option value="3">Every three years</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const BlockE = ({ inputs, togglePillar }) => (
  <div data-testid="calc-block-e" className="rounded-2xl border border-slate-200 bg-white card-soft p-6 lg:p-7">
    <p className="eyebrow text-slate-400">Which of these is broken right now?</p>
    <div className="mt-5 space-y-3">
      {PILLARS.map((p) => {
        const on = inputs.pillars.includes(p.n);
        return (
          <label
            key={p.n}
            data-testid={`calc-pillar-toggle-${p.n}`}
            className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${
              on ? "border-[#16A06A]/40 bg-[#E7F6EE]/40" : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <input
              type="checkbox"
              checked={on}
              onChange={() => togglePillar(p.n)}
              className="mt-1 h-4 w-4 accent-[#16A06A]"
            />
            <div className="flex-1">
              <div className="flex items-baseline gap-2">
                <span className="text-[13px] font-bold text-[#16A06A]">{p.n}</span>
                <span className="text-[15px] font-semibold text-[#0A1628]">{p.name}</span>
              </div>
              <p className="mt-1 text-[13px] leading-relaxed text-slate-500">{p.desc}</p>
            </div>
          </label>
        );
      })}
    </div>
  </div>
);

const BlockF = ({ inputs, update }) => (
  <div data-testid="calc-block-f" className="rounded-2xl border border-slate-200 bg-white card-soft p-6 lg:p-7">
    <p className="eyebrow text-slate-400">Your business</p>
    <div className="mt-5">
      <label className="text-[14px] text-[#0A1628] font-medium">How big is the team?</label>
      <select
        data-testid="calc-team-size"
        value={inputs.teamSize}
        onChange={(e) => update({ teamSize: e.target.value })}
        className="mt-2 w-full max-w-sm rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-[14px] text-[#0A1628] focus:border-[#16A06A] focus:ring-1 focus:ring-[#16A06A]/30 focus:outline-none"
      >
        {TEAM_SIZES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
    </div>
  </div>
);

/* ------------------------------ Calculator core ------------------------- */

const CalculatorBody = ({ emailEnabled }) => {
  const [inputs, setInputs] = useState({
    mode: "mix",
    ownerHoursPerWeek: 8,
    ownerHourlyValue: 75,
    burdenPct: 0.43,
    staff: [DEFAULT_STAFF_ROW()],
    softwarePerMonth: 0,
    workspacePerMonth: 0,
    turnover: { enabled: false, costPerReplacement: null, yearsBetween: null },
    pillars: ["01", "02"],
    teamSize: "5-15",
  });

  const update = useCallback((patch) => setInputs((s) => ({ ...s, ...patch })), []);
  const updateStaff = useCallback((idx, patch) => {
    setInputs((s) => ({ ...s, staff: s.staff.map((r, i) => (i === idx ? { ...r, ...patch } : r)) }));
  }, []);
  const addStaff = useCallback(() => {
    setInputs((s) => (s.staff.length >= 5 ? s : { ...s, staff: [...s.staff, DEFAULT_STAFF_ROW()] }));
  }, []);
  const removeStaff = useCallback((idx) => {
    setInputs((s) => ({ ...s, staff: s.staff.filter((_, i) => i !== idx) }));
  }, []);
  const updateTurnover = useCallback((patch) => setInputs((s) => ({ ...s, turnover: { ...s.turnover, ...patch } })), []);
  const togglePillar = useCallback((n) => {
    setInputs((s) => ({ ...s, pillars: s.pillars.includes(n) ? s.pillars.filter((p) => p !== n) : [...s.pillars, n].sort() }));
  }, []);

  const results = useMemo(() => computeResults(inputs), [inputs]);

  // Mobile expander state
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <section data-testid="calc-body" className="pb-16 lg:pb-24">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-7 space-y-5">
            <BlockA mode={inputs.mode} setMode={(m) => update({ mode: m })} />
            {inputs.mode !== "in-house" && <BlockB inputs={inputs} update={update} />}
            {inputs.mode !== "self" && (
              <BlockC
                inputs={inputs}
                update={update}
                updateStaff={updateStaff}
                addStaff={addStaff}
                removeStaff={removeStaff}
              />
            )}
            <BlockD inputs={inputs} update={update} updateTurnover={updateTurnover} />
            <BlockE inputs={inputs} togglePillar={togglePillar} />
            <BlockF inputs={inputs} update={update} />
          </div>
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <ResultsPanel inputs={inputs} results={results} emailEnabled={emailEnabled} />
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile sticky summary bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 shadow-[0_-8px_24px_rgba(15,23,42,0.08)]">
        <button
          type="button"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="calc-mobile-summary-btn"
          className="w-full flex items-center justify-between gap-3 px-5 py-3.5"
        >
          <div className="text-left">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
              {results.recommendedPlan === "Enterprise" ? "Recommended" : "The difference"}
            </div>
            <div className="text-[15px] font-bold text-[#0A1628]">
              {results.recommendedPlan}
              {results.annualDifference != null && results.annualDifference > 0 && (
                <span className="text-[#16A06A]"> · {fmtMoney(results.annualDifference)}/yr</span>
              )}
            </div>
          </div>
          <ChevronDown size={18} className={`text-slate-400 transition-transform ${mobileOpen ? "rotate-180" : ""}`} />
        </button>
        {mobileOpen && (
          <div className="max-h-[70vh] overflow-y-auto p-5 border-t border-slate-100">
            <ResultsPanel inputs={inputs} results={results} emailEnabled={emailEnabled} />
          </div>
        )}
      </div>
    </section>
  );
};

/* --------------------------------- Page --------------------------------- */

export default function Calculator() {
  useReveal();
  const [emailEnabled, setEmailEnabled] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    document.title = "Back Office Cost Calculator | RevGen";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "Work out what your back office really costs — your hours, your admin pay, employer burden and turnover — and see it against a flat monthly plan from $1,500. No email required."
      );
    }
    axios
      .get(`${API}/calculator-email/status`)
      .then((r) => setEmailEnabled(!!r.data?.enabled))
      .catch(() => setEmailEnabled(false));
  }, []);

  return (
    <div data-testid="calculator-page" className="bg-white text-[#0A1628]">
      <Navbar />
      <main>
        <Hero />
        <CalculatorBody emailEnabled={emailEnabled} />
        <Sources />
        <WeEmploy />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
